// Мок api.cabinet: история просмотров, «Мои покупки», возвраты и отзывы
// веб-кабинета. Фикстуры строятся из каталога моков, состояние — в памяти.
import type { BuyerCoupons } from "@osmix/contracts/promo-partners";
import type {
	BuyerPendingReview,
	BuyerPurchase,
	BuyerReturnDetail,
	BuyerReview,
	CabinetApi,
	ReturnAttachment,
	ReturnLine,
} from "../cabinet";
import { mockProducts } from "./data";

const delay = (ms = 180) => new Promise((r) => setTimeout(r, ms));
const DAY = 86_400_000;
const at = (daysAgo: number) => new Date(Date.now() - daysAgo * DAY).toISOString();
const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const photoOf = (i: number) => mockProducts[i % mockProducts.length]?.photos[0];

const purchases: BuyerPurchase[] = mockProducts.slice(0, 7).map((p, i) => ({
	orderId: `ord_mock_${Math.floor(i / 2) + 1}`,
	orderCreatedAt: at(10 + Math.floor(i / 2) * 3),
	orderTotal: p.price * 2,
	orderLineId: `L${i % 2}`,
	productId: p.id,
	shopId: p.shopId,
	name: p.name,
	brand: p.brand,
	photo: p.photos[0],
	variant: p.sizes[0],
	size: p.sizes[0],
	color: p.color,
	quantity: 1,
	unitPrice: p.price,
	status: i === 1 || i === 3 ? "returned" : "delivered",
	statusAt: at(6 + i),
	returnableQuantity: i === 1 || i === 3 ? 0 : 1,
	returnId: undefined,
	canDispute: i === 5,
}));

const line = (i: number, qty: number, reason: ReturnLine["reasonCode"]): ReturnLine => {
	const p = mockProducts[i]!;
	return {
		id: uid("orl"),
		orderLineId: "L0",
		productId: p.id,
		name: p.name,
		photo: p.photos[0],
		variant: p.sizes[0],
		quantity: qty,
		unitPrice: p.price,
		refund: p.price * qty,
		reasonCode: reason,
		comment: "Шов на левом рукаве неаккуратно прошит, нитки торчат.",
		cancelledAt: undefined,
		attachments: [1, 2, 3].map((n) => attachment("buyer", photoOf(i + n))),
	};
};

function attachment(author: "buyer" | "seller", url?: string): ReturnAttachment {
	return {
		id: uid("rat"),
		author,
		kind: "photo",
		url: url ?? "",
		thumbnailUrl: url,
		createdAt: at(1),
	};
}

const baseReturn = (over: Partial<BuyerReturnDetail>): BuyerReturnDetail => ({
	id: uid("ort"),
	orderId: "ord_mock_1",
	shopName: "Мастерская Анны",
	kind: "return",
	reason: "Брак",
	status: "requested",
	uiStatus: "under_review",
	refundTotal: 0,
	requestedRefund: 0,
	reasonCode: "defect",
	createdAt: at(3),
	updatedAt: at(3),
	lines: [],
	attachments: [],
	...over,
});

const returns: BuyerReturnDetail[] = [
	baseReturn({ lines: [line(0, 1, "defect"), line(2, 1, "defect")] }),
	baseReturn({
		status: "approved",
		uiStatus: "awaiting_shipment",
		lines: [line(4, 1, "not_suitable")],
		shipment: {
			deadlineAt: at(-12),
			address: "г. Москва, ул. Фестивальная, д. 64, кв. 345",
			carrier: "СДЭК",
			refundTargetLabel: "Денежные средства будут возвращены на тот счет, которым была произведена оплата.",
		},
	}),
	baseReturn({
		status: "rejected",
		uiStatus: "dispute_open",
		lines: [line(5, 1, "defect")],
		dispute: {
			id: uid("ord"),
			status: "open",
			buyerComment: "Прошу рассмотреть",
			sellerText:
				"Здравствуйте! Благодарим за обращение. Сожалеем о возникшей ситуации. Однако после проверки предоставленных фото мы не можем признать указанные дефекты браком.",
			openedAt: at(2),
		},
		attachments: [attachment("seller", photoOf(8)), attachment("seller", photoOf(9))],
	}),
	baseReturn({
		status: "refunded",
		uiStatus: "returned",
		lines: [line(6, 1, "damaged")],
		refundedAt: at(1),
	}),
];
for (const r of returns) r.requestedRefund = r.lines.reduce((s, l) => s + l.refund, 0);

const reviews: BuyerReview[] = [
	{
		id: "orv_mock_1",
		orderId: "ord_mock_2",
		productId: mockProducts[3]?.id,
		productName: mockProducts[3]?.name,
		productPhoto: photoOf(3),
		brand: mockProducts[3]?.brand,
		rating: 5,
		text: "Отличная вещь — тёплая, лёгкая и идеально сидит.",
		variant: "черный, 46",
		anonymous: false,
		media: [
			{ url: photoOf(3) ?? "", kind: "video" },
			{ url: photoOf(4) ?? "", kind: "photo" },
		],
		sellerReply: "Здравствуйте, благодарим за отзыв!",
		createdAt: at(8),
	},
];

let pending: BuyerPendingReview[] = mockProducts.slice(0, 3).map((p, i) => ({
	productId: p.id,
	orderId: `ord_mock_${i + 1}`,
	name: p.name,
	brand: p.brand,
	price: p.price,
	image: p.photos[0],
	variant: p.sizes[0],
	size: p.sizes[0],
	orderedAt: at(12),
}));

const tabOf = (r: BuyerReturnDetail) => (r.dispute ? "dispute" : r.status === "refunded" || r.status === "rejected" ? "archive" : "active");

const find = (id: string) => {
	const r = returns.find((x) => x.id === id);
	if (!r) throw new Error("Заявка не найдена");
	return r;
};

export const mockCabinet: CabinetApi = {
	viewHistory: async () => {
		await delay();
		return mockProducts.slice(0, 10).map((p, i) => ({
			productId: p.id,
			name: p.name,
			brand: p.brand,
			price: p.price,
			image: p.photos[0],
			viewedAt: at(i * 0.3),
		}));
	},
	purchases: async () => {
		await delay();
		return purchases;
	},
	returnable: async (orderId) => {
		await delay();
		return {
			orderId,
			lines: purchases
				.filter((p) => p.orderId === orderId && p.returnableQuantity > 0)
				.map((p) => ({
					orderLineId: p.orderLineId,
					productId: p.productId,
					name: p.name,
					photo: p.photo,
					variant: p.variant,
					orderedQuantity: p.quantity,
					availableQuantity: p.returnableQuantity,
					unitPrice: p.unitPrice,
				})),
		};
	},
	uploadReturnAttachment: async (file) => {
		await delay();
		return attachment("buyer", URL.createObjectURL(file));
	},
	createReturn: async (input) => {
		await delay();
		const created = baseReturn({ orderId: input.orderId, createdAt: new Date().toISOString() });
		created.lines = input.lines.map((l) => {
			const p = purchases.find((x) => x.orderId === input.orderId && x.orderLineId === l.orderLineId);
			if (p) p.returnableQuantity = Math.max(0, p.returnableQuantity - l.quantity);
			if (p) p.status = "return_pending";
			return {
				id: uid("orl"),
				orderLineId: l.orderLineId,
				productId: p?.productId ?? "",
				name: p?.name ?? "",
				photo: p?.photo,
				variant: p?.variant,
				quantity: l.quantity,
				unitPrice: p?.unitPrice ?? 0,
				refund: (p?.unitPrice ?? 0) * l.quantity,
				reasonCode: l.reason,
				comment: l.comment ?? "",
				attachments: [],
			};
		});
		created.requestedRefund = created.lines.reduce((s, l) => s + l.refund, 0);
		returns.unshift(created);
	},
	returns: async (tab) => {
		await delay();
		return returns.filter((r) => tabOf(r) === tab);
	},
	returnDetail: async (id) => {
		await delay();
		return find(id);
	},
	cancelReturn: async (id, keepLineIds, reduce = []) => {
		await delay();
		const r = find(id);
		const keep = new Set(keepLineIds);
		for (const cut of reduce) {
			const l = r.lines.find((x) => x.id === cut.lineId);
			if (l && keep.has(l.id)) {
				l.quantity = Math.max(1, l.quantity - cut.quantity);
				l.refund = l.unitPrice * l.quantity;
			}
		}
		for (const l of r.lines) if (!keep.has(l.id) && !l.cancelledAt) l.cancelledAt = new Date().toISOString();
		if (keep.size === 0) {
			r.status = "rejected";
		}
		r.requestedRefund = r.lines.filter((l) => !l.cancelledAt).reduce((s, l) => s + l.refund, 0);
		return r;
	},
	openDispute: async (id, input) => {
		await delay();
		const r = find(id);
		r.dispute = {
			id: uid("ord"),
			status: "open",
			buyerComment: input.comment,
			openedAt: new Date().toISOString(),
		};
		r.uiStatus = "dispute_open";
		return r;
	},
	closeDispute: async (id) => {
		await delay();
		const r = find(id);
		if (r.dispute) r.dispute = { ...r.dispute, status: "closed", closedAt: new Date().toISOString() };
		r.uiStatus = "dispute_closed";
		return r;
	},
	reviews: async () => {
		await delay();
		return reviews;
	},
	pendingReviews: async () => {
		await delay();
		return pending;
	},
	uploadReviewMedia: async (file) => {
		await delay();
		return {
			url: URL.createObjectURL(file),
			kind: file.type.startsWith("video/") ? "video" : "photo",
		};
	},
	createReview: async (input) => {
		await delay();
		const p = pending.find((x) => x.productId === input.productId);
		pending = pending.filter((x) => x.productId !== input.productId);
		const review: BuyerReview = {
			id: uid("orv"),
			orderId: input.orderId ?? p?.orderId ?? "",
			productId: input.productId,
			productName: p?.name,
			productPhoto: p?.image,
			brand: p?.brand,
			rating: input.rating,
			text: input.text ?? "",
			variant: p?.variant,
			anonymous: Boolean(input.anonymous),
			media: input.media ?? [],
			createdAt: new Date().toISOString(),
		};
		reviews.unshift(review);
		return review;
	},
	// «Мои купоны» офлайн: один публичный код, сохранять можно любой.
	coupons: async () => {
		await delay();
		return mockCoupons();
	},
	saveCoupon: async (code) => {
		await delay();
		const normalized = code.trim().toUpperCase();
		if (!savedCoupons.includes(normalized)) savedCoupons.push(normalized);
		return mockCoupons();
	},
	removeCoupon: async (code) => {
		await delay();
		savedCoupons = savedCoupons.filter((c) => c !== code.trim().toUpperCase());
		return mockCoupons();
	},
};

let savedCoupons: string[] = [];
// Поля карточки (title, headline, conditions …) в живом API собирает шлюз.
const couponCard = {
	firstOrderOnly: false,
	newToShopOnly: false,
	shopId: null,
	expiresLabel: null,
	conditions: [] as string[],
	status: "active" as const,
	statusMessage: null,
};
const mockCoupons = (): BuyerCoupons => ({
	available: [
		{
			...couponCard,
			code: "OSMIX10",
			kind: "percent",
			value: 0.1,
			minSubtotal: 0,
			description: "Скидка OSMIX",
			startsAt: null,
			expiresAt: null,
			source: "public",
			shopName: null,
			usesLeft: 1,
			restricted: false,
			title: "Скидка OSMIX",
			headline: "-10%",
		},
		...savedCoupons.map((code) => ({
			...couponCard,
			code,
			kind: "percent" as const,
			value: 0.05,
			minSubtotal: 0,
			description: "",
			startsAt: null,
			expiresAt: null,
			source: "saved" as const,
			shopName: null,
			usesLeft: 1,
			restricted: false,
			title: code,
			headline: "-5%",
		})),
	],
	ended: [],
	history: [],
});
